import Vue from 'vue';
import { getEntityByKeyList } from '../utils';

/**
 * Factory function to create isolated store instances for each FormBuilder component
 * @param {string} instanceId - Unique identifier for the store instance
 * @returns {Object} Store instance with state and mutations
 */
export function createFormBuilderStore(instanceId) {
  // Create isolated reactive state for this instance
  const state = Vue.observable({
    instanceId,
    isDirty: false,
    case_id: '',
    list: [],
    dependencies: {},
    linked_blocks: [],
    personal_details: null,
    gap_errors: null,
    gap_values: {},
    utils: {},
    readOnlyMode: false,
  });

  // Create mutations bound to this instance's state
  const mutations = {
    setIsDirty(payload = true) {
      state.isDirty = payload;
    },
    
    handleGapErrors(payload) {
      const storageKey = `gapErrorValue_${instanceId}_${payload.id}`;
      let checkgapError = localStorage.getItem(storageKey);
      if (checkgapError == undefined || checkgapError || checkgapError == null) {
        localStorage.setItem(storageKey, JSON.stringify(payload));
      }
      state.gap_errors = payload;
      if (!payload?.gaps) return;
      state.gap_errors.gaps.forEach(mutations.setGapGroup);
    },
    
    setGapGroup(payload) {
      // Use 'block_id' prop as a group identifier
      // Use 'block_fe_id' as a particular timeline identifier
      Vue.set(state.gap_values, payload.block_id, {
        ...payload,
        timeline: payload.timeline.map(gap => {
          if (gap.invalid_gap) return gap;
          const relatedBlock = getEntityByKeyList(state.list, { block_fe_id: gap.block_fe_id });
          const relatedField = getEntityByKeyList(relatedBlock.entities, { field_id: gap.field_id });
          return {
            ...gap,
            block: relatedBlock,
            field: relatedField,
          }
        }),
      });
      const storageKey = `gapTimeline_${instanceId}_${payload?.block_id}`;
      localStorage.setItem(storageKey, JSON.stringify(payload));
    },
    
    setFilledGap(field, block, gap) {
      // for filled gaps we will use values from related fields
      const newGap = {
        ...gap,
        frontend: true,
        block,
        field,
        invalid_gap: false,
      }
      state.gap_values[block.block_id].timeline.push(newGap);
    },

    /**
     * @param {*} sec_id uuid of the section, you want to change status of
     * @param {*} status status of the section, ERROR, PENDING, COMPLETED etc,.
     */
    setSectionStatus(sec_id, sectionStatus, statusProp) {
      let index = state.list.findIndex(el => el.section_id === sec_id)
      Vue.set(state.list, index, {...state.list[index], [statusProp]: sectionStatus})
    },

    /**
     * @param {Object} block - block you want to re-order
     * @param {String} section_id - the section's id in which the block exists
     * @param {String} direction - can be up or down depending on the block's position in the block-group-list
     */
    handleOrder(block, section_id, direction) {
      const moveInArray = function (arr, from, to) {
        // Make sure a valid array is provided
        if (Object.prototype.toString.call(arr) !== '[object Array]') {
          throw new Error('Please provide a valid array');
        }
        // Delete the item from it's current position
        var item = arr.splice(from, 1);
        // Make sure there's an item to move
        if (!item.length) {
          throw new Error('There is no item in the array at index ' + from);
        }
        // Move the item to its new position
        arr.splice(to, 0, item[0]);
        arr.forEach((item, index) => {
          item.serial = index + 1;
        });
      };

      const sectionToChange = state.list.find(sec => sec.section_id === section_id);
      const blockToOrderIndex = sectionToChange.entities.findIndex(blc => blc.block_fe_id === block.block_fe_id)
      const indexToMove = direction === 'up' ? blockToOrderIndex - 1 : blockToOrderIndex + 1
      sectionToChange.entities[blockToOrderIndex].serial = sectionToChange.entities[indexToMove].serial + (direction === 'up' ? -1 : 1)
      moveInArray(sectionToChange.entities, indexToMove, blockToOrderIndex);
    },

    setReadonlyMode(payload) {
      state.readOnlyMode = payload;
    },

    // Cleanup method to remove localStorage items for this instance
    cleanup() {
      // Remove all localStorage items for this instance
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes(`_${instanceId}_`) || key.endsWith(`_${instanceId}`))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  };

  // Return the store instance
  return {
    state,
    mutations,
    
    // Getters for compatibility
    get isDirty() { return state.isDirty; },
    get case_id() { return state.case_id; },
    get list() { return state.list; },
    get dependencies() { return state.dependencies; },
    get linked_blocks() { return state.linked_blocks; },
    get personal_details() { return state.personal_details; },
    get gap_errors() { return state.gap_errors; },
    get gap_values() { return state.gap_values; },
    get utils() { return state.utils; },
    get readOnlyMode() { return state.readOnlyMode; },
    
    // Setters for compatibility
    set case_id(val) { state.case_id = val; },
    set list(val) { state.list = val; },
    set dependencies(val) { state.dependencies = val; },
    set linked_blocks(val) { state.linked_blocks = val; },
    set personal_details(val) { state.personal_details = val; },
    set gap_errors(val) { mutations.handleGapErrors(val); },
    set gap_values(val) { state.gap_values = val; },
    set utils(val) { state.utils = val; },
    set readOnlyMode(val) { mutations.setReadonlyMode(val); },
  };
}
